import { useEffect, useRef, useState } from 'react'
import { Box, IconButton, Popover, Slider, Tooltip, Typography } from '@mui/material'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded'
import HeightRoundedIcon from '@mui/icons-material/HeightRounded'
import { EDVCSessionsCompletedDetailsComponent } from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingComponents'

const CTX_EVENT = 'calendar-filter-context'
const REQUEST_EVENT = 'calendar-request-filter-context'
const SET_FILTER_EVENT = 'calendar-set-filter'
const SET_CANCELLED_EVENT = 'calendar-set-cancelled'
const SET_REPORTS_EVENT = 'calendar-set-reports'
const REFRESH_EVENT = 'calendar-refresh'
const SET_SLOT_DURATION_EVENT = 'calendar-set-slot-duration'

const SLOT_MIN = 5
const SLOT_MAX = 120

const EMPTY_CTX = {
  bookedSlotsInDay: [],
  selectedFilterType: 'all',
  cancelledCount: 0,
  showCancelledOnly: false,
  showCancelledChip: false,
  includeReportRelatedData: false,
  hideToggleSwitchForReportRelatedData: true,
  eachSlotDurationInMins: 30,
  slotDurationApplicable: false,
}

/**
 * Slim filter strip that lives as a second row of the main page header. It holds the pills, an
 * explicit Refresh, and (on the Calendar tab) a "more" menu with the Slot Duration control — the
 * actions that used to sit in the calendar's floating-header 3-dots.
 *
 * It owns no calendar state: it mirrors the calendar's context over the same event bridge the date
 * navigator uses (calendar broadcasts {@code calendar-filter-context}; this strip emits
 * {@code calendar-set-*} / {@code calendar-refresh}).
 */
export default function BookingFilterStrip() {
  const [ctx, setCtx] = useState(EMPTY_CTX)
  const [moreAnchor, setMoreAnchor] = useState(null)
  const [localSlot, setLocalSlot] = useState(30)
  const slotSyncRef = useRef(true)

  useEffect(() => {
    const onCtx = (e) => {
      if (e?.detail) setCtx((prev) => ({ ...prev, ...e.detail }))
    }
    window.addEventListener(CTX_EVENT, onCtx)
    window.dispatchEvent(new CustomEvent(REQUEST_EVENT))
    return () => window.removeEventListener(CTX_EVENT, onCtx)
  }, [])

  // Keep the slider in step with the calendar unless the user is mid-drag.
  useEffect(() => {
    if (slotSyncRef.current && typeof ctx.eachSlotDurationInMins === 'number') {
      setLocalSlot(ctx.eachSlotDurationInMins)
    }
  }, [ctx.eachSlotDurationInMins])

  const emit = (name, value) => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(name, { detail: { value } }))
    }
  }
  const resolve = (updater, current) => (typeof updater === 'function' ? updater(current) : updater)

  const iconBtnSx = {
    color: 'rgba(67, 56, 202, 0.7)',
    backgroundColor: 'rgba(67, 56, 202, 0.05)',
    border: '1px solid rgba(67, 56, 202, 0.15)',
    padding: '5px',
    '&:hover': {
      backgroundColor: 'rgba(67, 56, 202, 0.1)',
      borderColor: 'rgba(67, 56, 202, 0.3)',
      color: 'rgba(67, 56, 202, 1)',
    },
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.25, width: '100%' }}>
      <EDVCSessionsCompletedDetailsComponent
        stacked
        noMargins
        compactUI
        bookedSlotsInDay={ctx.bookedSlotsInDay}
        selectedFilterType={ctx.selectedFilterType}
        setSelectedFilterType={(t) => emit(SET_FILTER_EVENT, t)}
        cancelledCount={ctx.cancelledCount}
        showCancelledChip={ctx.showCancelledChip}
        showCancelledOnly={ctx.showCancelledOnly}
        setShowCancelledOnly={(u) => emit(SET_CANCELLED_EVENT, resolve(u, ctx.showCancelledOnly))}
        includeReportRelatedData={ctx.includeReportRelatedData}
        hideToggleSwitchForReportRelatedData={ctx.hideToggleSwitchForReportRelatedData}
        setIncludeReportRelatedData={(u) => emit(SET_REPORTS_EVENT, resolve(u, ctx.includeReportRelatedData))}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
        <Tooltip title="Refresh" arrow>
          <IconButton size="small" onClick={() => emit(REFRESH_EVENT)} sx={iconBtnSx}>
            <RefreshRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        {ctx.slotDurationApplicable && (
          <Tooltip title="More" arrow>
            <IconButton size="small" onClick={(e) => setMoreAnchor(e.currentTarget)} sx={iconBtnSx}>
              <MoreVertRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Popover
        open={Boolean(moreAnchor)}
        anchorEl={moreAnchor}
        onClose={() => setMoreAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ px: 2, py: 1.5, width: 240 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
            <HeightRoundedIcon sx={{ fontSize: 18, color: 'rgba(67, 56, 202, 0.7)' }} />
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'rgba(15,23,42,0.85)' }}>
              Slot duration
            </Typography>
            <Typography sx={{ ml: 'auto', fontSize: '13px', fontWeight: 700, color: 'rgba(67,56,202,0.9)' }}>
              {localSlot} min
            </Typography>
          </Box>
          <Slider
            size="small"
            min={SLOT_MIN}
            max={SLOT_MAX}
            step={5}
            value={localSlot}
            onChange={(e, v) => {
              slotSyncRef.current = false
              setLocalSlot(v)
            }}
            onChangeCommitted={(e, v) => {
              slotSyncRef.current = true
              emit(SET_SLOT_DURATION_EVENT, v)
            }}
            sx={{ color: '#4338ca' }}
          />
          <Typography sx={{ fontSize: '10.5px', color: 'rgba(100,116,139,0.85)' }}>
            Controls the calendar grid granularity.
          </Typography>
        </Box>
      </Popover>
    </Box>
  )
}
